import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { getPrimaryContactPoint } from '../../../address-book/contactPoints';
import type { ClientSummary } from '../../useData';
import api from '../../../../services/api';

interface ClientRelationshipMapProps {
  clientSummary: ClientSummary;
}

interface ContactNode {
  id: number;
  name: string;
  email?: string;
}

function toRecordList(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as { data: unknown[] }).data)) {
    return (raw as { data: Record<string, unknown>[] }).data;
  }
  return [];
}

const ClientRelationshipMap: React.FC<ClientRelationshipMapProps> = ({ clientSummary }) => {
  const { colors } = useTheme();
  const [contacts, setContacts] = useState<ContactNode[]>([]);
  const [loading, setLoading] = useState(false);
  const clientId = clientSummary.clientId;
  const counts = clientSummary._count ?? {};

  const loadContacts = useCallback(async () => {
    const contactCount = counts.crm_ClientsContacts ?? 0;
    if (contactCount === 0) {
      setContacts([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/client-contacts/all_of/${clientId}`);
      const records = toRecordList(res.data).slice(0, 5);
      setContacts(
        records.map((r) => {
          const name =
            [r.clientContactFirstName, r.clientContactLastName].filter(Boolean).join(' ').trim() ||
            'Unnamed contact';
          const email = getPrimaryContactPoint(
            r.contactPoints as import('../../../address-book/contactPoints').ContactPoint[] | undefined,
            'EMAIL',
          )?.value;
          return { id: r.clientContactId as number, name, email };
        }),
      );
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, counts.crm_ClientsContacts]);

  useEffect(() => {
    void loadContacts();
  }, [loadContacts]);

  const orgEmail = getPrimaryContactPoint(clientSummary.contactPoints, 'EMAIL')?.value;
  const appointmentCount = counts.clientAppointments ?? 0;
  const todoCount = counts.clientTodos ?? 0;
  const emailCount = counts.linkedEmails ?? 0;

  const taskLabel =
    appointmentCount + todoCount > 0
      ? `${appointmentCount} appt${appointmentCount !== 1 ? 's' : ''}, ${todoCount} todo${todoCount !== 1 ? 's' : ''}`
      : 'No tasks yet';

  return (
    <div className={cn('rounded-xl border p-4', colors.border, colors.background)}>
      <h3 className={cn('text-sm font-semibold mb-1', colors.text)}>Relationship map</h3>
      <p className={cn('text-xs mb-4', colors.textSecondary)}>
        How this organization connects to people, messages, and work.
      </p>

      <div className="relative flex flex-col items-center gap-4 py-2">
        {/* Organization hub */}
        <div
          className={cn(
            'relative z-10 w-full max-w-[220px] rounded-xl border-2 px-4 py-3 text-center shadow-sm',
            colors.border,
            'border-blue-500/40 bg-blue-500/5 dark:bg-blue-400/10',
          )}
        >
          <p className={cn('text-[10px] font-semibold uppercase tracking-wider', colors.textSecondary)}>Organization</p>
          <p className={cn('text-sm font-semibold truncate', colors.text)} title={clientSummary.clientName}>
            {clientSummary.clientName || `Org #${clientId}`}
          </p>
          {orgEmail ? (
            <p className={cn('text-xs truncate mt-0.5', colors.textSecondary)} title={orgEmail}>
              {orgEmail}
            </p>
          ) : null}
        </div>

        {/* Connector lines */}
        <div className="w-px h-4 bg-current opacity-20" aria-hidden />

        {/* Satellite nodes */}
        <div className="grid grid-cols-1 gap-3 w-full">
          {/* People */}
          <div className={cn('rounded-lg border p-3', colors.border, colors.surfaceHover)}>
            <div className="flex items-center justify-between mb-2">
              <span className={cn('text-xs font-semibold uppercase tracking-wide', colors.textSecondary)}>People</span>
              <span className={cn('text-xs tabular-nums font-medium', colors.text)}>{counts.crm_ClientsContacts ?? 0}</span>
            </div>
            {loading ? (
              <p className={cn('text-xs', colors.textSecondary)}>Loading…</p>
            ) : contacts.length > 0 ? (
              <ul className="space-y-1.5">
                {contacts.map((c) => (
                  <li key={c.id} className="flex items-start gap-2 min-w-0">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-500 shrink-0" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className={cn('text-xs font-medium truncate', colors.text)}>{c.name}</p>
                      {c.email ? (
                        <p className={cn('text-[11px] truncate', colors.textSecondary)} title={c.email}>
                          ↔ {c.email}
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={cn('text-xs', colors.textSecondary)}>No contacts linked</p>
            )}
          </div>

          {/* Emails */}
          <div className={cn('rounded-lg border p-3', colors.border, colors.surfaceHover)}>
            <div className="flex items-center justify-between">
              <span className={cn('text-xs font-semibold uppercase tracking-wide', colors.textSecondary)}>Emails</span>
              <span className={cn('text-xs tabular-nums font-medium', colors.text)}>{emailCount}</span>
            </div>
            <p className={cn('text-xs mt-1', colors.textSecondary)}>
              {emailCount > 0 ? 'Linked to this organization' : 'No linked messages'}
            </p>
            {emailCount > 0 ? (
              <button
                type="button"
                onClick={() => document.getElementById('records-linked-emails')?.scrollIntoView({ behavior: 'smooth' })}
                className={cn('text-xs font-medium mt-1 hover:underline', colors.text)}
              >
                View emails ↓
              </button>
            ) : null}
          </div>

          {/* Tasks */}
          <div className={cn('rounded-lg border p-3', colors.border, colors.surfaceHover)}>
            <div className="flex items-center justify-between">
              <span className={cn('text-xs font-semibold uppercase tracking-wide', colors.textSecondary)}>Tasks</span>
              <span className={cn('text-xs tabular-nums font-medium', colors.text)}>
                {appointmentCount + todoCount}
              </span>
            </div>
            <p className={cn('text-xs mt-1', colors.textSecondary)}>{taskLabel}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {appointmentCount > 0 ? (
                <Link to={`/appointments/new?clientId=${clientId}`} className={cn('text-xs font-medium hover:underline', colors.text)}>
                  + Appointment
                </Link>
              ) : null}
              {todoCount > 0 || appointmentCount === 0 ? (
                <Link to={`/todos/new?clientId=${clientId}`} className={cn('text-xs font-medium hover:underline', colors.text)}>
                  + Todo
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientRelationshipMap;
